import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'

type Variant = 'bordered' | 'bare' | 'flat'

interface RailSectionProps {
  title: string
  viewAllHref?: string
  viewAllLabel?: string
  trailing?: ReactNode
  variant?: Variant
  children: ReactNode
  className?: string
}

/**
 * Hub's section + card primitive — one source of truth for the right-rail
 * widgets, Explore shelves, and inline inserts.
 *
 * Renders:
 *   1. A header row (small green square + uppercase mono label + View All ↗)
 *   2. A card body shell that wraps children
 *
 *   bordered (default) — Hub's `rounded-xl` + translucent gradient + ghost inset border
 *   bare              — header only; children render their own card(s)
 *   flat              — same gradient/border but no `overflow-hidden` (for shelves
 *                       whose children include hover/focus states that overflow)
 */
export const RailSection = ({
  title,
  viewAllHref,
  viewAllLabel = 'View All ↗',
  trailing,
  variant = 'bordered',
  children,
  className,
}: RailSectionProps) => {
  const cardBody =
    variant === 'bare' ? (
      children
    ) : (
      <div
        className={
          variant === 'flat'
            ? 'rounded-xl'
            : 'rounded-xl overflow-hidden'
        }
        style={{
          background:
            'linear-gradient(180deg, rgba(10,13,15,0.55) 0%, rgba(5,7,9,0.55) 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.07)',
        }}
      >
        {children}
      </div>
    )

  return (
    <section className={className}>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className="w-1 h-1 bg-[#02f305]" aria-hidden />
          <h2 className="text-[10px] font-mono uppercase tracking-[0.42em] text-white/55">
            {title}
          </h2>
        </div>
        {trailing ??
          (viewAllHref && (
            <Link
              to={viewAllHref}
              className="text-[9px] font-mono uppercase tracking-[0.28em] text-[#02f305]/75 hover:text-[#02f305] transition-colors"
            >
              {viewAllLabel}
            </Link>
          ))}
      </div>
      {cardBody}
    </section>
  )
}
